import React from 'react'
import get from 'lodash/get'
import type { SelectInlineInputProps } from '@ztechapps/webui.input'
import { SelectInlineInput } from '@ztechapps/webui.input'
import type { UnitOfMeasure } from '@ztechapps/common.contract'
import { UnitOfMeasureSelect } from '../select/UnitOfMeasureSelect'
import { useUnitOfMeasureSelectOptions } from '../select/useUnitOfMeasureSelectOptions'

export type ContextUomSelectInlineInputProps = Partial<SelectInlineInputProps> & {
  category?: string
  /** The field name for the conversion factor (e.g., 'orderConversionFactor') */
  factorFieldName: string
  /** The base UOM to calculate conversion factor against */
  baseUom: UnitOfMeasure | null | undefined
}

/**
 * Custom inline input for context UOM (order, purchase, workOrder) that:
 * When changed, also calculates and sets the conversion factor automatically
 * Formula: contextUom.conversionFactor / baseUom.conversionFactor
 */
export const ContextUomSelectInlineInput = ({
  category,
  name,
  initialValues,
  factorFieldName,
  baseUom,
  ...rest
}: ContextUomSelectInlineInputProps) => {
  const options = useUnitOfMeasureSelectOptions({ category })

  // RelatedEntity: flatten resolved object to _id string for matching
  const resolvedObject = get(initialValues, name)
  const flattenedId = resolvedObject?._id ?? resolvedObject
  const flattenedInitialValues = {
    ...initialValues,
    [name]: flattenedId,
  }

  return (
    <SelectInlineInput<ContextUomSelectInlineInputProps>
      {...rest}
      name={name}
      initialValues={flattenedInitialValues}
      selectComponent={UnitOfMeasureSelect}
      selectOptions={options}
      displayContent={() => {
        if (!resolvedObject) return null
        return `${resolvedObject.name} (${resolvedObject.symbol})`
      }}
      updateParams={({ updatedValue, _id }) => {
        const uomRef = updatedValue?.value ? { _id: updatedValue.value } : null

        // Calculate the conversion factor
        // Formula: 1 [contextUom] = (contextUom.conversionFactor / baseUom.conversionFactor) [baseUom]
        let calculatedFactor: number | null = null

        if (updatedValue?.data && baseUom) {
          const contextUomData = updatedValue.data as UnitOfMeasure

          // Only auto-calculate if same category and baseUom has valid conversion factor
          if (contextUomData.category === baseUom.category && baseUom.conversionFactor) {
            calculatedFactor = contextUomData.conversionFactor / baseUom.conversionFactor
          } else {
            // Cross-category: default to 1, user must set manually
            calculatedFactor = 1
          }
        } else if (uomRef === null) {
          // Cleared the UOM, set factor to null
          calculatedFactor = null
        }

        return {
          input: {
            _id,
            [name]: uomRef,
            [factorFieldName]: calculatedFactor,
          },
        }
      }}
    />
  )
}
